import type { ReactNode } from 'react';
import { Button, Loading, Tag, Toggle } from '@carbon/react';
import { Close, Edit } from '@carbon/react/icons';
import { ColorSwatch, DEFAULT_COLORS } from 'components/color-picker';
import { format0 } from 'utils/number';
import { formatRangeSummary, valuesToTags } from './utils';
import type { AppliedFilter, ColorRule, ColorTarget } from './types';
import styles from './filter-cards.module.scss';

interface RuleCardShellProps {
  /** Attribute display label shown in the header. */
  label: string;
  /** Header-right action buttons (Edit / Remove, plus a toggle for color rules). */
  actions: ReactNode;
  /** The card body — value tags, a range summary, or color rows. */
  children: ReactNode;
}

/**
 * Shared chrome for the applied-rule cards (mock §4b/§4c): a header (label +
 * actions) over a body. Both `FilterCard` and the P2-T18 `ColorRuleCard` compose
 * it so the card anatomy lives in one place. Pure — no atoms, no callbacks of its
 * own.
 *
 * @param {RuleCardShellProps} props - The label, header actions, and body.
 * @return {JSX.Element} The card shell.
 */
export function RuleCardShell({
  label,
  actions,
  children,
}: RuleCardShellProps) {
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.label} title={label}>
          {label}
        </span>
        <div className={styles.actions}>{actions}</div>
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  );
}

interface FilterCardProps {
  /** The applied filter this card represents. */
  filter: AppliedFilter;
  /** Resolved attribute display label — the caller maps attribute → label (card stays catalog-free). */
  label: string;
  /** Optional unit appended to a range summary (e.g. "ft"); omitted ⇒ no suffix. */
  unit?: string;
  /** Reopen the modal focused on this filter's attribute. */
  onEdit: () => void;
  /** Delete this filter (the panel dispatches remove by id). */
  onRemove: () => void;
}

/**
 * Kind-aware body for a `FilterCard`: a categorical filter renders its selected
 * values as gray tags (≤3, then a "+N more" tag via `valuesToTags`); a range
 * filter renders a single min–max summary tag via `formatRangeSummary`
 * (reflecting open-ended bounds). The overflow count is comma-formatted (value
 * lists can run to the thousands).
 *
 * @param {object} props - The filter and the optional range unit.
 * @return {JSX.Element} The card body content.
 */
function FilterCardBody({
  filter,
  unit,
}: {
  filter: AppliedFilter;
  unit?: string;
}) {
  if (filter.kind === 'categorical') {
    const values = filter.selectedValues ?? [];
    const { tags, overflow } = valuesToTags(values);
    return (
      <>
        {tags.map((value) => (
          <Tag key={value} type="gray">
            {value}
          </Tag>
        ))}
        {overflow > 0 && <Tag type="gray">+{format0(overflow)} more</Tag>}
      </>
    );
  }

  const summary = filter.range
    ? formatRangeSummary(filter.range, filter.kind, unit)
    : '';
  return <Tag type="gray">{summary}</Tag>;
}

/**
 * An applied dataset-filter card (mock §4b): header with the attribute label +
 * Edit / Remove actions, over a kind-aware body (value tags for categorical, a
 * min–max summary for ranges). Pure + prop-driven — the panel wires `onEdit` /
 * `onRemove` to the modal / atom. (The color-rule card lands in P2-T18.)
 *
 * @param {FilterCardProps} props - The filter, its label, optional unit, and the action handlers.
 * @return {JSX.Element} The filter card.
 */
export function FilterCard({
  filter,
  label,
  unit,
  onEdit,
  onRemove,
}: FilterCardProps) {
  return (
    <RuleCardShell
      label={label}
      actions={
        <>
          <Button
            kind="ghost"
            hasIconOnly
            size="sm"
            renderIcon={Edit}
            iconDescription={`Edit ${label}`}
            tooltipPosition="bottom"
            onClick={onEdit}
          />
          <Button
            kind="ghost"
            hasIconOnly
            size="sm"
            renderIcon={Close}
            iconDescription={`Remove ${label}`}
            tooltipPosition="bottom"
            onClick={onRemove}
          />
        </>
      }
    >
      <FilterCardBody filter={filter} unit={unit} />
    </RuleCardShell>
  );
}

/** Display label per color target id (P2 ships the Map target only). */
const TARGET_LABELS: Record<ColorTarget, string> = {
  map: 'Map',
};

interface ColorRuleCardProps {
  /** The color rule this card represents. */
  rule: ColorRule;
  /** Resolved attribute display label — the caller maps attribute → label. */
  label: string;
  /** Optional unit appended to a range summary (e.g. "ft"); omitted ⇒ no suffix. */
  unit?: string;
  /** Reopen the modal (color mode) focused on this rule's attribute. */
  onEdit: () => void;
  /** Delete this rule (the panel dispatches remove by id). */
  onRemove: () => void;
  /** Set the rule's `enabled` to the next value (controlled toggle — not a blind flip). */
  onEnabledChange: (enabled: boolean) => void;
  /** Recolor one categorical value's swatch from the card (no modal); omitted ⇒ no-op. */
  onValueColorChange?: (value: string, color: string) => void;
  /** Recolor the range swatch from the card; omitted ⇒ no-op. */
  onRangeColorChange?: (color: string) => void;
  /** This rule's `map_elements` resolution state (panel-resolved); undefined ⇒ nothing shown. */
  colorStatus?: 'loading' | 'error' | 'ready';
  /** Re-run this rule's color call(s) (the error Retry). */
  onRetry?: () => void;
}

/**
 * One swatch + value row in a color rule's body. The swatch is the interactive
 * `ColorSwatch` (the same picker the modal uses), so a value (or the range) can be
 * recolored straight from the card — clicking opens the palette / hex popover and
 * `onColorChange` commits the pick. Recoloring changes the rule's color, not its
 * values — when each value keeps its own color (the common case) the map_elements
 * group key is unchanged, so the ids are cached and the map just repaints; values
 * that share a color re-partition the groups and re-resolve.
 *
 * @param {object} props - The chip color, the recolor callback, and the row's value/summary content.
 * @return {JSX.Element} The swatch + value row.
 */
function ColorRow({
  color,
  onColorChange,
  loading = false,
  children,
}: {
  color?: string;
  onColorChange: (color: string) => void;
  /** Show a small inline spinner after the value while the rule's colors resolve. */
  loading?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={styles.colorRow}>
      {/* ColorSwatch reports (color, opacity); P2 has no opacity control, so the
          second arg is ignored. It needs a defined color to parse, so an unset color
          (shouldn't happen — the modal auto-assigns) falls back to a default. */}
      <ColorSwatch
        color={color ?? DEFAULT_COLORS[0]}
        onChange={(next) => onColorChange(next)}
      />
      <span className={styles.colorValue}>{children}</span>
      {loading && (
        <Loading
          small
          withOverlay={false}
          description="Resolving colors"
          className={styles.colorRowSpinner}
        />
      )}
    </div>
  );
}

/**
 * An applied color-rule card (mock §4c): header (attribute label + Edit / Remove)
 * over an "Active on:" row (the rule's target tags + an Enable⇄Disable toggle) and
 * the color rows — one swatch+value per categorical value, or a single swatch for a
 * range. A disabled rule is shown by the toggle's "Disabled" state (no dimming —
 * the toggle conveys it). The rule's
 * `map_elements` resolution `colorStatus` + `onRetry` are panel-resolved and
 * rendered here (a small inline spinner beside each swatch while loading / an
 * inline error + Retry) — the card stays pure +
 * prop-driven, the panel wires the callbacks to the atom. (Duplicate is deferred —
 * one rule per attribute.)
 *
 * @param {ColorRuleCardProps} props - The rule, its label, optional unit, the action handlers, and the color status.
 * @return {JSX.Element} The color-rule card.
 */
export function ColorRuleCard({
  rule,
  label,
  unit,
  onEdit,
  onRemove,
  onEnabledChange,
  onValueColorChange,
  onRangeColorChange,
  colorStatus,
  onRetry,
}: ColorRuleCardProps) {
  return (
    <RuleCardShell
      label={label}
      actions={
        <>
          <Button
            kind="ghost"
            hasIconOnly
            size="sm"
            renderIcon={Edit}
            iconDescription={`Edit ${label}`}
            tooltipPosition="bottom"
            onClick={onEdit}
          />
          <Button
            kind="ghost"
            hasIconOnly
            size="sm"
            renderIcon={Close}
            iconDescription={`Remove ${label}`}
            tooltipPosition="bottom"
            onClick={onRemove}
          />
        </>
      }
    >
      <div className={styles.colorBody}>
        <div className={styles.activeOnRow}>
          <span className={styles.activeOnLabel}>Active on:</span>
          {rule.applyTo.map((target) => (
            <Tag key={target} type="blue">
              {TARGET_LABELS[target]}
            </Tag>
          ))}
          <Toggle
            id={`eval3-color-toggle-${rule.id}`}
            className={styles.toggle}
            size="sm"
            // Visible state text via labelA/labelB (Carbon swaps them on `toggled`).
            // No `labelText`/`hideLabel`, so there's no top label and the side label
            // falls through to these state words; `aria-label` is the accessible name
            // (the side text is aria-hidden; `aria-checked` conveys state to AT).
            aria-label="Toggle coloring"
            labelA="Disabled"
            labelB="Enabled"
            toggled={rule.enabled}
            onToggle={(checked) => onEnabledChange(checked)}
          />
        </div>

        <div className={styles.colorRows}>
          {rule.kind === 'categorical' ? (
            (rule.selectedValues ?? []).map((value) => (
              <ColorRow
                key={value}
                color={rule.valueColors?.[value]}
                loading={colorStatus === 'loading'}
                onColorChange={(color) => onValueColorChange?.(value, color)}
              >
                {value}
              </ColorRow>
            ))
          ) : (
            <ColorRow
              color={rule.rangeColor}
              loading={colorStatus === 'loading'}
              onColorChange={(color) => onRangeColorChange?.(color)}
            >
              {rule.range
                ? formatRangeSummary(rule.range, rule.kind, unit)
                : ''}
            </ColorRow>
          )}
        </div>

        {colorStatus === 'error' && (
          <div className={styles.colorError}>
            <span className={styles.colorErrorText}>
              Couldn’t resolve colors
            </span>
            {onRetry && (
              <Button kind="ghost" size="sm" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        )}
      </div>
    </RuleCardShell>
  );
}
